import { AngularFireAuth } from '@angular/fire/auth';
import { OrderRequest } from '../models/order-request.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, of, BehaviorSubject } from 'rxjs';
import { FirebaseService } from './firebase.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { AppTrial } from '../models/app-trial.model';

import { CanActivate } from '@angular/router';
import { first, take, timestamp } from 'rxjs/operators';
import * as firebase from 'firebase';
import { CrashService } from './crash.service';
import { AnalyticsService } from './analytics.service';

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(private router: Router, private auth: AuthService) {}

  async canActivate(): Promise<boolean> {
    const user = await this.auth.getUser();
    const loggedIn = !!user;

    if (loggedIn) {
      console.log('logged in at root, get their data');
      const userDoc = await this.auth.getUserDoc(user);
      console.log('canActivate says user doc is', userDoc);

      if (userDoc.currentTeamId) {
        this.router.navigate([`/messages/${userDoc.currentTeamId}/list`]);
      } else {
        this.router.navigate(['/teams']);
      }
    }
    return true;
  }
}

export interface User {
  uid?: string;
  analyticsId?: string;
  phone?: PhoneNumber;
  settings?: Settings;
  email?: string;
  firstName?: string;
  lastName?: string;
  config?: any;
  eula?: boolean;
  hasTeams?: boolean;
  hasArchivedTeams?: boolean;
  trial?: AppTrial;
  currentTeamId?: string;
  orderRequest?: OrderRequest;
  userCreatedAt?: any;
  userPushToken?: string;
  // userPushTokens?: any[];
}

export interface AuthData {
  phone: PhoneNumber;
  password: string;
}

export interface PhoneNumber {
  countryTag: string;
  country: string;
  area: string;
  prefix: string;
  line: string;
  friendly: string;
  full: string;
}

export interface Settings {
  pushNotifications?: boolean;
  emailMarketing?: boolean;
  smsMarketing?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  debug: false;
  windowRef: any;
  verificationCode: string;

  user$: Observable<any>;
  subUser: Subscription;
  userDataSubscription: Subscription;
  userPushToken = null;

  private opSubject = new BehaviorSubject(null);
  op$ = this.opSubject.asObservable();
  op = 'splash';

  private userDataSubject = new BehaviorSubject(null);
  userData$ = this.userDataSubject.asObservable();
  userData: User;

  constructor(
    private crash: CrashService,
    private fbs: FirebaseService,
    public afAuth: AngularFireAuth,
    public translate: TranslateService,
    public toastController: ToastController,
    private router: Router,
  ) {
    console.log(
      `push token is: ${this.userPushToken ? this.userPushToken : 'none'}`
    );

    this.userDataSet(this.defaultUser());
    this.subUser = this.fbs.user$.subscribe(
      async (firebaseUser) => {
        console.log('user change detected');
        if (firebaseUser) {
          console.log('logged in user is', firebaseUser);
          if (
            this.userData.uid !== firebaseUser.uid ||
            this.userData.phone.full === ''
          ) {
            console.log(
              `user changed from ${this.userData.uid} to ${firebaseUser.uid}`,
            );
            this.getUserData(firebaseUser, false);
          } else {
            console.log('already knew that');
          }
        } else {
          console.log('we have NO logged in user');
          this.crash.setUserId('unknown');
          this.clearUserData();
          return of(null);
        }
      },
      (error: any) => {
        this.crash.subscription('auth-service', 'subUser', error);
      },
    );
  }

  setPushToken(token) {
    this.userPushToken = token;
  }

  getUser(): Promise<any> {
    return this.afAuth.authState.pipe(first()).toPromise();
  }

  getUserDoc(user): Promise<User> {
    return this.fbs.afs
      .doc<User>(`users/${user.uid}`)
      .valueChanges()
      .pipe(first())
      .toPromise();
  }

  defaultPhone(environment) {
    const prod: PhoneNumber = {
      countryTag: 'us',
      country: '',
      area: '',
      prefix: '',
      line: '',
      friendly: '',
      full: '',
    };
    const test: PhoneNumber = {
      countryTag: 'us',
      country: '',
      area: '',
      prefix: '',
      line: '',
      friendly: '',
      full: '',
    };
    return environment === 'test' ? test : prod;
  }

  setPhone(phoneNumber) {
    this.userData.phone = phoneNumber;
    this.userDataSubject.next(this.userData);
  }

  // Formats phone numbers as E.164
  get e164PhoneNumber() {
    console.log(this.userData);

    const num =
      this.userData.phone.country +
      this.userData.phone.area +
      this.userData.phone.prefix +
      this.userData.phone.line;
    return `${num}`;
  }

  defaultSettings(): Settings {
    const settings: Settings = {
      pushNotifications: true,
      emailMarketing: true,
      smsMarketing: true,
    };
    return settings;
  }

  newAnalyticsId(): string {
    // Alphanumeric characters
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let autoId = '';
    for (let i = 0; i < 20; i++) {
      autoId += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return autoId;
  }

  defaultUser(): User {
    return {
      uid: null,
      analyticsId: this.newAnalyticsId(),
      phone: this.defaultPhone('prod'),
      firstName: '',
      lastName: '',
      email: '',
      eula: false,
      hasTeams: false,
      settings: this.defaultSettings(),
      currentTeamId: null,
      trial: { status: 'none', expires: null },
      userCreatedAt: this.fbs.timestamp,
      userPushToken: this.userPushToken ? this.userPushToken : 'none',
    };
  }

  clearUserData() {
    this.userDataSet(this.defaultUser());
  }

  userDataSet(data) {
    console.log('userDataSet(data)', data);
    console.log(
      'userDataSet(token)',
      data.userPushToken ? data.userPushToken : 'none'
    );
    this.userData = data;
    this.userDataBroadcast(data);
  }

  userDataBroadcast(data) {
    this.userDataSubject.next(data);
  }

  broadcastOp(op) {
    this.opSubject.next(op);
  }

  checkPushTokens() {
    console.log('checkPushTokens()');

    // let userPushTokens = [];
    // let isDirty = true;

    // // If we previously had a push token array, use that instead.
    // if (this.userData?.userPushTokens) {
    //   userPushTokens = this.userData.userPushTokens;
    // }

    // // Check if this token is actually new
    // _.forEach(userPushTokens, function (token) {
    //   if (token === token) {
    //     isDirty = false;
    //   }
    // });

    // // If it is new, update it.
    // if (isDirty) {
    //   userPushTokens.push(newToken);
    //   const tokens: User = { userPushTokens };
    //   await this.setToken(tokens);
    // }
  }

  getUserData(user, verified) {
    console.log('getUserData()');
    this.userDataSubscription = this.fbs.afs
      .doc<User>(`users/${user.uid}`)
      .valueChanges()
      .subscribe(
        async (userData) => {
          if (!userData) {
            console.log(
              'have no stored user data, default it to locally collected data',
            );
            userData = this.userData;
            userData.uid = user.uid;
            userData.userPushToken = this.userPushToken;
            this.updateUser(userData);
            this.crash.setUserId(userData.analyticsId);
            this.router.navigate(['/auth/info']);
            return;
          } else {
            this.crash.setUserId(userData.analyticsId);
            if (this.userPushToken) {
              if (userData.userPushToken != this.userPushToken) {
                console.log(`updating push token to ${this.userPushToken}`);

                userData.userPushToken = this.userPushToken;
                this.userPushToken = null;
                this.updateUser({ userPushToken: userData.userPushToken });
              }
            }
            console.log('already have user data, use it', userData);
            if (userData.uid) {
              if (!userData.firstName || !userData.lastName) {
                // Need a user name
                this.router.navigate(['/auth/info']);
                return;
              }
            }
          }
          this.userDataSet(userData);
        },
        (error: any) => {
          this.crash.subscription(
            'auth-service',
            'userDataSubscription',
            error,
          );
        },
      );
  }

  async updateUser(changes) {
    try {
      const userId = this.fbs.userId;
      if (userId) {
        console.log(`auth.updateUser() - got user id '${userId}'`);
        return await this.fbs.updateUser(userId, changes);
      }
      console.log(`auth.updateUser() - no user yet`);
      return of(null).toPromise();
    } catch (error) {
      console.log(`auth.updateUser() - error`, error);
      return of(null).toPromise();
    }
  }

  userDataRemove(uid): Promise<void> {
    return this.fbs.delete(`/users/${uid}`);
  }

  signout(archive = false) {
    this.crash.setCustomKey('user_action', 'signout');
    this.userDataSubscription.unsubscribe();
    this.verificationCode = '';
    this.op = 'signin';
    if (archive) {
      this.userDataRemove(this.userData.uid).then(() => {
        console.log('removed user, sign out');
        this.deleteAccount();
        this.fbs.signout();
      });
    } else {
      this.clearUserData();
      this.fbs.signout();
    }
    this.router.navigate(['/']);
  }

  deleteAccount() {
    try {
      this.fbs.deleteAccount();
    } catch (error) {
      console.log('Failed to delete account', error);
      this.crash.process(
        'auth-service',
        'deleteAccount',
        `Failed to delete account`,
        null,
      );
    }
  }
}
